import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

/**
 * Sitewide CTA components. Locked to the standard set by Jo (Feb 2026):
 *   - Primary button label EVERYWHERE: "Check if we're a fit"
 *   - Primary button link EVERYWHERE:  /book/
 *   - Microcopy directly under the button, small text:
 *       "Free 30-min call · we'll map your podcast + visibility plan"
 *   - Secondary CTA (text link near the button):
 *       "See what's included" -> /podcast-visibility-package/ (or a same-page
 *        anchor when the button already lives on that page).
 *
 * Do NOT use "Book a free 30-min call", "Get a quote", or
 * "You're 30 minutes away from a podcast" as button text anywhere.
 */

// -----------------------------------------------------------------------------
// Wording constants — imported by CTABlock and reusable if a caller needs a
// custom layout while still guaranteeing the canonical strings.
// -----------------------------------------------------------------------------
export const CTA_PRIMARY_TEXT = "Check if we're a fit";
export const CTA_PRIMARY_HREF = '/book/';
export const CTA_MICROCOPY =
  "Free 30-min call · we'll map your podcast + visibility plan";
export const CTA_SECONDARY_TEXT = "See what's included";
export const CTA_SECONDARY_HREF = '/podcast-visibility-package/';

/**
 * PrimaryCTA — the single primary button style used across the v2 site.
 * Defaults are now the sitewide-locked wording and destination so any
 * existing caller that does not pass overrides immediately conforms.
 */
export function PrimaryCTA({
  to = CTA_PRIMARY_HREF,
  children,
  size = 'md',
  className = '',
  ...rest
}) {
  const sizes = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg',
  };
  return (
    <Link
      to={to}
      data-testid={rest['data-testid'] || 'cta-primary'}
      className={`inline-flex items-center justify-center gap-2 font-semibold text-white bg-[#BC516F] hover:bg-[#9A3A54] rounded-[10px] shadow-[0_10px_30px_rgba(188,81,111,0.25)] hover:shadow-[0_14px_36px_rgba(154,58,84,0.35)] transition ${sizes[size]} ${className}`}
      {...rest}
    >
      {children || (
        <>
          {CTA_PRIMARY_TEXT} <ArrowRight className="w-5 h-5" />
        </>
      )}
    </Link>
  );
}

export function SecondaryCTA({ to, children, size = 'md', className = '', ...rest }) {
  const sizes = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg',
  };
  return (
    <Link
      to={to}
      data-testid={rest['data-testid']}
      className={`inline-flex items-center justify-center gap-2 font-semibold text-[#1E2A44] bg-white border-2 border-[#1E2A44] hover:bg-[#1E2A44] hover:text-white rounded-[10px] transition ${sizes[size]} ${className}`}
      {...rest}
    >
      {children}
    </Link>
  );
}

/**
 * CTABlock — the sitewide standard CTA unit: primary button + microcopy +
 * secondary "See what's included" text link. Use this at every hero,
 * pricing section and final CTA site-wide.
 *
 * Props:
 *   size            – 'sm' | 'md' | 'lg' (button size, default 'lg')
 *   align           – 'left' | 'center' (default 'left')
 *   testIdPrefix    – prefix for data-testid attrs (default 'cta')
 *   secondaryHref   – override the secondary link target. Pass a hash
 *                     (e.g. '#whats-included') on the /podcast-visibility-package
 *                     page itself so the secondary link never points at the
 *                     page a visitor is already on.
 *   secondaryText   – override the secondary label if the anchor is not
 *                     literally "See what's included" (rare — the current
 *                     value is fine everywhere per spec).
 *   hideSecondary   – hide the secondary link entirely.
 *   invert          – true on dark navy backgrounds; switches microcopy +
 *                     secondary link to a light palette.
 */
export function CTABlock({
  size = 'lg',
  align = 'left',
  testIdPrefix = 'cta',
  secondaryHref = CTA_SECONDARY_HREF,
  secondaryText = CTA_SECONDARY_TEXT,
  hideSecondary = false,
  invert = false,
  className = '',
}) {
  const alignCls = align === 'center' ? 'items-center text-center' : 'items-start';
  const microTone = invert ? 'text-white/80' : 'text-[#64748B]';
  const secondaryTone = invert
    ? 'text-white hover:text-[#D07C93]'
    : 'text-[#1E2A44] hover:text-[#BC516F]';

  const isAnchor = typeof secondaryHref === 'string' && secondaryHref.startsWith('#');

  return (
    <div
      className={`flex flex-col gap-2.5 ${alignCls} ${className}`}
      data-testid={`${testIdPrefix}-block`}
    >
      <PrimaryCTA size={size} data-testid={`${testIdPrefix}-primary`}>
        {CTA_PRIMARY_TEXT} <ArrowRight className="w-5 h-5" />
      </PrimaryCTA>
      <p
        className={`text-sm leading-snug ${microTone}`}
        data-testid={`${testIdPrefix}-microcopy`}
      >
        {CTA_MICROCOPY}
      </p>
      {!hideSecondary && (
        isAnchor ? (
          <a
            href={secondaryHref}
            className={`text-sm font-semibold underline decoration-2 underline-offset-4 transition ${secondaryTone}`}
            data-testid={`${testIdPrefix}-secondary`}
          >
            {secondaryText} →
          </a>
        ) : (
          <Link
            to={secondaryHref}
            className={`text-sm font-semibold underline decoration-2 underline-offset-4 transition ${secondaryTone}`}
            data-testid={`${testIdPrefix}-secondary`}
          >
            {secondaryText} →
          </Link>
        )
      )}
    </div>
  );
}
